// Live data for the Compta screen: loads from the API and maps it into the
// shapes the design's logic works with (month tiles, rows, "today paid", …).
import type { DocumentCategory, DocumentStatusContract, DocumentTypeContract, PayMode, PermissionKey } from '@rimon/contracts';
import { DOCUMENT_CATEGORIES, DOCUMENT_PERMISSION_KEYS } from '@rimon/contracts';
import { api, apiAll } from '../api/client';
import { level, session } from '../api/auth';
import { applyDiscount, sumCents, toCents, toDecimal } from '../api/money';
import { todayIso } from '../screens/import/format';
import { keepTexts } from '../i18n/data-texts';
import { statusView, type StatusView } from './status';

// ---------- API shapes (see ../backend/docs/FRONTEND_INTEGRATION.md) ----------
export interface ApiDocument {
  id: string;
  version: number;
  type: DocumentTypeContract;
  status: DocumentStatusContract;
  documentNumber: string | null;
  issuerName: string | null;
  documentDate: string | null;
  dueDate: string | null;
  accountingMonth?: string | null;
  totalAmount: string | null;
  netAmount?: string | null; vatAmount?: string | null;
  effectiveAmount: string | null;
  communication: string | null;
  settlement?: Settlement | null;
  /** Primary PDF + attachments (role primary | attachment). */
  files?: Array<{ fileId: string; role: string; file?: { originalFilename?: string | null; mimeType?: string | null } | null }>;
  notes?: string | null;
  employeeId?: string | null;
  importJobId?: string | null;
  primaryFileId: string | null;
  createdAt?: string;
  invoiceDetail: { hasDiscount: boolean; discountRate: string | null; discountDeadline: string | null; payMode?: PayMode | null; amountToPay?: string | null } | null;
  /** Credit notes only: amount still open to allocate (≥ 0, whatever sign the total is stored with). */
  openCredit?: string | null;
  /** Ticked against a statement booking (confirmed match): shown as „Abgeglichen“. */
  reconciled?: boolean;
}
export interface ApiQueueItem {
  id: string; version: number; type: DocumentTypeContract; category: 'in' | 'adm' | 'pay'; accountingMonth?: string | null;
  documentNumber: string | null; issuerName: string | null; documentDate: string | null; dueDate: string | null;
  totalAmount: string | null; creditAmount: string | null; effectiveAmount: string | null; suggestedAmount: string;
  communication: string | null; discount: { rate: string; deadline: string; active: boolean } | null; primaryFileId: string | null;
  plannedAmount?: string | null; plannedBank?: 'BIL' | 'BGL' | null; plannedCommunication?: string | null; plannedCreditNoteId?: string | null;
  /** „Montant à payer“ chosen at import / in the edit dialog (null = older invoice: suggestion). */
  payMode?: PayMode | null; amountToPay?: string | null;
  /** LP: the invoice's IBAN and its warning (new | changed) until confirmed by phone. */
  iban?: string | null; ibanAlert?: 'new' | 'changed' | null;
  /** BL3: net deviations from its RimoChantiers order, [de, fr] each. */
  orderAlerts?: Array<[string, string]>;
}
export interface ApiTodayPayment { id: string; amount: string; paidAt: string; communication: string | null; bank: 'BIL' | 'BGL'; document: { id: string; documentNumber: string | null; issuerName: string | null; type: DocumentTypeContract; version: number; status?: DocumentStatusContract; primaryFileId?: string | null } }
export interface ApiMonth { month: string; count: number; openCount: number; sum: string }
export interface ApiBankAccount { id: string; bank: 'BIL' | 'BGL' | 'OTHER'; bankName?: string | null; iban: string; bic: string | null; holder: string; defaultForPayment: boolean; active?: boolean; onDocuments?: boolean }
export interface ApiImportJob { id: string; source: string; destinationType: DocumentTypeContract; mode: 'AI' | 'MANUAL' | 'API'; status: string; name: string | null; createdAt: string; _count?: { items: number } }
export interface ApiClosing { id: string; month: string; status: 'OPEN' | 'CLOSED'; version: number; checklistItems: Array<{ id: string; key: string; label: string; status: 'OPEN' | 'DONE' | 'NOT_REQUIRED'; custom: boolean }> }
export interface ApiCompany { id: string; name: string; legalForm?: string | null; foundedAt?: string | null; address: string | null; postalCode: string | null; city: string | null; country: string; rcs: string | null; vatNumber: string | null; phone: string | null; email: string | null; website: string | null; foldMarks?: boolean }
export interface ApiUser { id: string; email: string; firstName: string; lastName: string; active: boolean; mustChangePassword: boolean; lastLoginAt: string | null; employee: { id: string } | null; roles: Array<{ roleId: string; role: { id: string; name: string } }> }
export interface ApiEmployee {
  id: string; firstName: string; lastName: string; function: string | null; matricule: string | null; email: string | null; active?: boolean; /** last working day when the employee left, "YYYY-MM-DD" */ leftAt?: string | null; /** first working day („Eintritt“) */ joinedAt?: string | null;
  user?: { id: string; email: string; active: boolean; mustChangePassword: boolean; roles: Array<{ roleId: string; role: { id: string; name: string } }> } | null;
}
export interface ApiRole { id: string; name: string; kind: 'SYSTEM' | 'CUSTOM'; systemKey: string | null; permissions: Array<{ key: string; level: 'NONE' | 'READ' | 'EDIT' }>; _count?: { users: number } }
export interface ApiCard { id: string; bank: 'BIL' | 'BGL' | 'OTHER'; type: string; last4: string; ownerId: string | null; active: boolean; /** last day of the printed month, "YYYY-MM-DD" */ expiresAt?: string | null; /** first day of the month from which the card is expected in the month close, "YYYY-MM-DD" (null = no start) */ validFrom?: string | null; expired?: boolean; owner?: ApiEmployee | null; instruments?: Array<{ id: string; deviceName: string; deviceCardLast4: string; ownerId: string | null; active: boolean }> }
export interface ApiSupplier { id: string; name: string; vatNumber: string | null; defaultDiscountRate: string | null; defaultDiscountDays: number | null; active: boolean; /** Authority (issuer of charges), 30.10.2026. */ authority?: boolean }
export interface ApiMatch { id: string; status: 'SUGGESTED' | 'CONFIRMED' | 'REJECTED'; totalScore: string | number; version: number; document: { id: string; documentNumber: string | null; issuerName: string | null; totalAmount: string | null; status: string } }
export interface ApiTransaction { id: string; bookingDate: string; amount: string; counterparty: string | null; communication: string | null; matched: boolean; missingReceipt?: boolean; missingReceiptNote?: string | null; matches: ApiMatch[] }
export interface ApiStatement { id: string; type: 'BANK_ACCOUNT' | 'CARD'; month: string; status: string; bankAccount?: { bank: string; iban: string } | null; corporateCard?: { bank: string; last4: string } | null; transactions?: ApiTransaction[] }
export interface ApiChecklistTemplate { id: string; key: string; label: string; active: boolean; sortOrder: number }

// ---------- labels (API values stay English, only the display is translated) ----------
/** Workflow keys of incoming invoices / Abgaben (Vorbereitung, Bezahlung). Chips per type: data/status.ts. */
export const STATUS_LABEL: Record<DocumentStatusContract, string> = {
  RECEIVED: 'Reçu', PROBLEM: 'Problème', IN_PROGRESS: 'En cours', PRINTED: 'Imprimé', PAID: 'Payé', ARCHIVED: 'Archiviert',
};
const MN: Record<string, string> = { '01': 'Januar', '02': 'Februar', '03': 'März', '04': 'April', '05': 'Mai', '06': 'Juni', '07': 'Juli', '08': 'August', '09': 'September', '10': 'Oktober', '11': 'November', '12': 'Dezember' };

/** Date-only fields arrive as "2026-09-18T00:00:00.000Z" → first 10 chars. */
export const ddmmyyyy = (iso: string | null | undefined) => {
  if (!iso) return '';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${d}.${m}.${y}`;
};
export const hhmm = (iso: string) => new Date(iso).toTimeString().slice(0, 5);
/** Days from a to b; undefined when one is missing (escompte without deadline, e.g. imported from the old system). */
const dayDiff = (a: string | null | undefined, b: string | null | undefined) => (a && b ? Math.round((Date.parse(b.slice(0, 10)) - Date.parse(a.slice(0, 10))) / 86_400_000) : undefined);

const CATS = Object.keys(DOCUMENT_CATEGORIES) as DocumentCategory[];
/** The design's "Rechnungen · Alle" tab merges these categories. */
const ALL_INV: DocumentCategory[] = ['in', 'out', 'bel', 'nc'];
const sees = (k: PermissionKey) => level(session.user, k) !== 'NONE';
export const canCat = (c: DocumentCategory) => sees(DOCUMENT_PERMISSION_KEYS[DOCUMENT_CATEGORIES[c]]);

// ---------- store ----------
type Listener = () => void;
const listeners = new Set<Listener>();

export const live = {
  months: {} as Partial<Record<DocumentCategory, ApiMonth[]>>,
  rows: {} as Partial<Record<DocumentCategory | 'payE', Record<string, ApiDocument[]>>>,
  workflow: [] as ApiDocument[], // incoming invoices RECEIVED / IN_PROGRESS
  queue: [] as ApiQueueItem[],
  today: [] as ApiTodayPayment[],
  creditNotes: [] as ApiDocument[],
  search: null as null | { q: string; docs: ApiDocument[] },
  accounts: [] as ApiBankAccount[],
  imports: [] as ApiImportJob[],
  employees: [] as ApiEmployee[],
  users: [] as ApiUser[], // all app accounts (Firma · Autres comptes)
  roles: [] as ApiRole[],
  cards: [] as ApiCard[],
  suppliers: [] as ApiSupplier[],
  templates: [] as ApiChecklistTemplate[],
  company: null as ApiCompany | null,
  closing: {} as Record<string, ApiClosing>,
  statements: {} as Record<string, ApiStatement[]>,
  payEmployees: [] as Array<{ employeeId: string; name: string; active: boolean; count: number; sum: string }>,
  loaded: false,
  version: 0,
};

function changed() {
  live.version++;
  const docs = [...live.workflow, ...live.creditNotes, ...(live.search?.docs ?? []), ...Object.values(live.rows).flatMap((byMonth) => Object.values(byMonth ?? {}).flat())];
  keepTexts([...docs.map((d) => d.issuerName), ...live.queue.map((q) => q.issuerName), ...live.today.map((p) => p.document.issuerName), ...live.suppliers.map((x) => x.name),
    ...live.employees.map((e) => `${e.firstName} ${e.lastName}`), ...live.payEmployees.map((e) => e.name)]);
  listeners.forEach((l) => l());
}

export function subscribe(l: Listener): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

const soft = <T,>(p: Promise<T>, fallback: T) => p.catch(() => fallback);

async function loadMonths() {
  const [entries, payEmployees] = await Promise.all([
    Promise.all(CATS.filter(canCat).map(async (c) => [c, await soft(api<ApiMonth[]>('/compta/documents/months', { query: { category: c } }), [])] as const)),
    canCat('pay') ? soft(api<typeof live.payEmployees>('/compta/documents/employees', { query: { category: 'pay' } }), []) : [],
  ]);
  live.months = Object.fromEntries(entries);
  live.payEmployees = payEmployees;
}

async function loadWorkflow() {
  if (!canCat('in')) return void (live.workflow = []);
  const [rec, prog] = await Promise.all(['RECEIVED', 'IN_PROGRESS'].map((status) => apiAll<ApiDocument>('/compta/documents', { category: 'in', status })));
  live.workflow = [...rec, ...prog];
}

async function loadPayments() {
  const [queue, today] = await Promise.all([
    sees('p_view') ? soft(api<ApiQueueItem[]>('/compta/payments/queue'), []) : [],
    sees('p_view') ? soft(api<ApiTodayPayment[]>('/compta/payments/today'), []) : [],
  ]);
  Object.assign(live, { queue, today });
}

/** (Re)loads everything the screens need, respecting the user's permissions. */
export async function loadAll(): Promise<void> {
  const [, , , , imports, creditNotes] = await Promise.all([
    loadMonths(),
    loadWorkflow(),
    loadPayments(),
    loadCore(false),
    sees('i_pdf') ? soft(api<ApiImportJob[]>('/compta/imports'), []) : [],
    canCat('nc') ? soft(apiAll<ApiDocument>('/compta/documents', { category: 'nc' }), []) : [],
  ]);
  Object.assign(live, { imports, creditNotes, rows: {}, loaded: true });
  changed();
}

/** After a status change / payment: refresh workflow, payment lists, counts and open month lists. */
export async function reloadDocs(): Promise<void> {
  const open = Object.entries(live.rows).flatMap(([c, byMonth]) => Object.keys(byMonth || {}).map((m) => [c as DocumentCategory | 'payE', m] as const));
  const [, , , creditNotes] = await Promise.all([loadMonths(), loadWorkflow(), loadPayments(),
    canCat('nc') ? soft(apiAll<ApiDocument>('/compta/documents', { category: 'nc' }), live.creditNotes) : []]);
  live.creditNotes = creditNotes;
  live.rows = {};
  await Promise.all(open.map(([c, m]) => loadRows(c, m, false)));
  changed();
}

/** Company master data (Firma tab + role simulation). */
export async function loadCore(notify = true): Promise<void> {
  const [employees, roles, cards, accounts, suppliers, templates, company, users] = await Promise.all([
    sees('f_emp') ? soft(api<ApiEmployee[]>('/core/employees'), live.employees) : [],
    sees('f_perm') ? soft(api<ApiRole[]>('/core/roles'), live.roles) : [],
    sees('f_cards') ? soft(api<ApiCard[]>('/core/cards'), live.cards) : [],
    sees('f_acct') ? soft(api<ApiBankAccount[]>('/core/bank-accounts'), live.accounts) : [],
    sees('f_esc') ? soft(api<ApiSupplier[]>('/core/suppliers'), live.suppliers)
      // Only the Émetteurs right: the same list from the Émetteurs page (⌘K group).
      : sees('f_emt') ? soft(api<Array<{ id: string; name: string; vatNumber: string | null; active: boolean; authority?: boolean; escompte: { rate: number; days: number } | null }>>('/compta/emetteurs')
        .then((l) => l.map((x) => ({ id: x.id, name: x.name, vatNumber: x.vatNumber, active: x.active, authority: !!x.authority, defaultDiscountRate: x.escompte ? String(x.escompte.rate) : null, defaultDiscountDays: x.escompte?.days ?? null }))), live.suppliers) : [],
    sees('f_abs') ? soft(api<ApiChecklistTemplate[]>('/core/closing-checklist-templates'), live.templates) : [],
    sees('f_data') ? soft(api<ApiCompany>('/core/company'), live.company) : null,
    sees('f_perm') ? soft(api<ApiUser[]>('/core/users'), live.users) : [],
  ]);
  Object.assign(live, { employees, roles, cards, accounts, suppliers, templates, company, users });
  if (notify) changed();
}

export async function reloadImports(): Promise<void> {
  live.imports = await api<ApiImportJob[]>('/compta/imports');
  changed();
}

const loading = new Set<string>();
/** Loads one month of one category (the document browser opens months lazily). */
export async function loadRows(cat: DocumentCategory | 'payE', month: string, notify = true): Promise<void> {
  const key = cat + '|' + month;
  if (loading.has(key)) return;
  loading.add(key);
  try {
    // payE: "month" is an employee id (Fiches de paie · Nach Mitarbeiter).
    const docs = await apiAll<ApiDocument>('/compta/documents', cat === 'payE' ? { category: 'pay', employeeId: month } : { category: cat, month });
    live.rows = { ...live.rows, [cat]: { ...(live.rows[cat] || {}), [month]: docs } };
    if (notify) changed();
  } finally {
    loading.delete(key);
  }
}

/** Global search over every document type the user may read (the design only showed incoming invoices). */
export async function search(q: string): Promise<void> {
  const docs = (await api<{ data: ApiDocument[] }>('/compta/documents', { query: { q, page: 1, pageSize: 100 } })).data;
  live.search = { q, docs };
  changed();
}

export async function loadClosing(month: string): Promise<ApiClosing> {
  const c = await api<ApiClosing>('/compta/closing', { query: { month } });
  live.closing = { ...live.closing, [month]: c };
  changed();
  return c;
}

/** Statements of a month with their transactions and match suggestions (Abschluss). */
export async function loadStatements(month: string): Promise<void> {
  if (!sees('d_ext')) return;
  const list = await api<ApiStatement[]>('/compta/statements', { query: { month } });
  const full = await Promise.all(list.map((st) => api<ApiStatement>('/compta/statements/' + st.id)));
  live.statements = { ...live.statements, [month]: full };
  changed();
}

export function setCompany(c: ApiCompany) {
  live.company = c;
  changed();
}

// ---------- mapping to the design's data model ----------
export interface Settlement {
  total: string; due: string; paid: string | null; lastPaidAt: string | null; paidVia: string | null; overpaid: string | null;
  credits: Array<{ creditNoteId: string; number: string | null; amount: string }>;
  deduction: { kind: 'escompte' | 'difference'; amount: string; percent: string } | null;
  /** Z.7: a customer's facture paid in part – still open. */
  open?: string | null;
}
export interface Row {
  id: string; no: string; sup: string; date: string; amt: number; cents: bigint; esc: boolean; delai?: number; rate: string;
  live: true; api: ApiDocument | ApiQueueItem | null; st0: string; version: number; job?: string | null; kind?: 'adm'; due?: string;
  label?: string; via?: string; at?: string; comm?: string; fileId?: string | null; suggested?: bigint; month?: string; plannedBank?: string; payMode?: PayMode;
  /** Full invoice total for display (amt/cents are after credit notes, for the payment logic). */
  total?: number;
  /** Total − credit notes − escompte = paid (backend settlement.ts). */
  settle?: Settlement | null;
  /** Chip per document type (data/status.ts); rows without it use the design's label colours. */
  view?: StatusView; type?: DocumentTypeContract; payId?: string;
}

const monthOf = (d: ApiDocument) => (d.accountingMonth || d.documentDate || d.createdAt || '').slice(0, 7);

/** Employee of a payslip. Without the Mitarbeiter right (f_emp) the payroll summary still carries the names. */
export const employeeName = (id: string | null | undefined): string | null => {
  if (!id) return null;
  const e = live.employees.find((x) => x.id === id);
  return e ? `${e.firstName} ${e.lastName}`.trim() : live.payEmployees.find((x) => x.employeeId === id)?.name || null;
};
/**
 * Status chip of a document (per type, see data/status.ts). `st0` stays the workflow key for incoming invoices and
 * Abgaben (Reçu / En cours / Imprimé / Payé: Vorbereitung and Bezahlung filter on it); other types carry their own label.
 */
export function rowStatus(d: Pick<ApiDocument, 'type' | 'status' | 'reconciled'>): { st0: string; view: StatusView } {
  const view = statusView(d.type, d.status, !!d.reconciled);
  return { view, st0: d.type === 'INCOMING_INVOICE' || d.type === 'TAX_OR_SOCIAL_CHARGE' ? STATUS_LABEL[d.status] : view.de };
}
export function toRow(d: ApiDocument): Row {
  const cents = toCents(d.effectiveAmount ?? d.totalAmount);
  const inv = d.invoiceDetail, esc = !!inv?.hasDiscount, date = d.documentDate ?? d.createdAt ?? '';
  return {
    // Payslips have no issuer: the column shows the employee (the list header reads MITARBEITER there).
    id: d.id, no: d.documentNumber || '—', sup: d.issuerName || (d.type === 'PAYROLL' ? employeeName(d.employeeId) : null) || '—', date: ddmmyyyy(date), amt: Number(cents) / 100, cents,
    total: d.totalAmount != null ? Number(toCents(d.totalAmount)) / 100 : undefined, settle: d.settlement ?? null,
    esc, rate: inv?.discountRate || '2', delai: esc && inv?.discountDeadline && date ? dayDiff(date, inv.discountDeadline) : undefined,
    live: true, api: d, ...rowStatus(d), version: d.version, job: d.importJobId,
    kind: d.type === 'TAX_OR_SOCIAL_CHARGE' ? 'adm' : undefined, due: ddmmyyyy(d.dueDate), label: d.notes || undefined,
    comm: d.communication || undefined, fileId: d.primaryFileId, month: monthOf(d), type: d.type,
  };
}

/** Payment queue entry → row. The server's suggestedAmount already applies credit notes and a running escompte. */
function queueRow(q: ApiQueueItem): Row {
  const cents = toCents(q.effectiveAmount ?? q.totalAmount), date = q.documentDate ?? '';
  return {
    id: q.id, no: q.documentNumber || '—', sup: q.issuerName || '—', date: ddmmyyyy(date), amt: Number(cents) / 100, cents,
    esc: !!q.discount, rate: q.discount?.rate || '2', delai: q.discount && date ? dayDiff(date, q.discount.deadline) : undefined,
    live: true, api: q, st0: 'Imprimé', version: q.version, kind: q.category === 'adm' ? 'adm' : undefined, due: ddmmyyyy(q.dueDate),
    comm: q.plannedCommunication || q.communication || undefined, fileId: q.primaryFileId, suggested: toCents(q.payMode ? q.suggestedAmount : q.plannedAmount ?? q.suggestedAmount), payMode: q.payMode ?? undefined, plannedBank: q.plannedBank || undefined,
    // Payslips: the period („Salaire 09/2026“ is the server's communication).
    type: q.type, month: (q.accountingMonth || q.documentDate || '').slice(0, 7) || undefined,
  };
}

function paidRow(p: ApiTodayPayment): Row {
  const cents = toCents(p.amount);
  return {
    id: p.document.id, no: p.document.documentNumber || '—', sup: p.document.issuerName || '—', date: '', amt: Number(cents) / 100, cents,
    esc: false, rate: '0', live: true, api: null, st0: 'Payé', version: p.document.version, kind: p.document.type === 'TAX_OR_SOCIAL_CHARGE' ? 'adm' : undefined,
    via: p.bank, at: hhmm(p.paidAt), comm: p.communication || undefined, suggested: cents, fileId: p.document.primaryFileId ?? null, payId: p.id, type: p.document.type,
  };
}

/** Discounted amount in cents for workflow invoices (queue rows carry the server's suggestion). */
export function suggestedCents(r: Row, today = new Date()): bigint {
  if (r.suggested !== undefined) return r.suggested;
  const inv = r.api && 'invoiceDetail' in r.api ? r.api.invoiceDetail : null;
  if (!r.esc || !inv?.discountDeadline || inv.discountDeadline.slice(0, 10) < todayIso(today)) return r.cents;
  return applyDiscount(r.cents, r.rate);
}

/** col2: header of the party column (issuer, customer, employee, account, authority), like the design's mock data. */
interface Cat { label: string; unit: string; long: string; col1: string; col2: string; months: any[]; rows: Record<string, Row[] | undefined> }

function monthTile(m: { month: string; count: number; cents: bigint }) {
  return { id: m.month, num: m.month.slice(5), title: MN[m.month.slice(5)] || m.month, year: m.month.slice(0, 4), count: m.count, sum: Number(m.cents) / 100 };
}

function category(c: DocumentCategory, meta: Omit<Cat, 'months' | 'rows'>): Cat {
  const months = (live.months[c] || []).map((m) => monthTile({ month: m.month, count: m.count, cents: toCents(m.sum) })).sort((a, b) => a.id.localeCompare(b.id));
  const rows: Cat['rows'] = {};
  for (const [m, docs] of Object.entries(live.rows[c] || {})) rows[m] = docs.map(toRow);
  return { ...meta, months, rows };
}

function merged(cs: DocumentCategory[], meta: Omit<Cat, 'months' | 'rows'>): Cat {
  const acc = new Map<string, { month: string; count: number; cents: bigint }>();
  for (const c of cs) for (const m of live.months[c] || []) {
    const a = acc.get(m.month) || { month: m.month, count: 0, cents: 0n };
    a.count += m.count;
    a.cents += toCents(m.sum);
    acc.set(m.month, a);
  }
  const months = [...acc.values()].map(monthTile).sort((a, b) => a.id.localeCompare(b.id));
  const rows: Cat['rows'] = {};
  // Also months that are loaded but empty for this choice (a month opened, then the choice changed): shows „Keine Treffer“.
  const loaded = new Set([...acc.keys(), ...cs.flatMap((c) => Object.keys(live.rows[c] || {}))]);
  for (const m of loaded) {
    const parts = cs.filter(canCat).map((c) => live.rows[c]?.[m]);
    if (parts.every(Boolean)) rows[m] = parts.flatMap((p) => p!.map(toRow));
  }
  return { ...meta, months, rows };
}

/** Which API categories a design category needs for one month. A combination of the Rechnungen choices is „bel+out“. */
export const catsFor = (ck: string): Array<DocumentCategory | 'payE'> =>
  ck === 'all' ? ALL_INV.filter(canCat) : ck.includes('+') ? (ck.split('+') as DocumentCategory[]).filter(canCat) : [ck as DocumentCategory | 'payE'];

const DIR_LABEL: Record<string, string> = { in: 'Eingehende', out: 'Ausgehende', bel: 'Belege' };

/** Builds the design's `D` (cats, sept, demo) from live data. `src` is static UI config. */
export function buildD(src: unknown) {
  const INV = { unit: 'Rechn.', long: 'Rechnungen', col1: 'RECHNUNG' };
  const PAY = { label: 'Fiches de paie', unit: 'Fiches', long: 'Fiches de paie', col1: 'FICHE', col2: 'MITARBEITER' };
  const ini = (name: string) => { const w = name.split(/\s+/).filter(Boolean); return ((w[0]?.[0] ?? '') + (w.length > 1 ? w[w.length - 1][0] : '')).toUpperCase() || '—'; };
  const cats: Record<string, Cat> = {
    all: merged(ALL_INV, { label: 'Rechnungen', ...INV, col2: 'ÉMETTEUR / KUNDE' }),
    in: category('in', { label: 'Eingehende Rechnungen', ...INV, col2: 'ÉMETTEUR' }),
    out: category('out', { label: 'Ausgehende Rechnungen', ...INV, col2: 'KUNDE' }),
    bel: category('bel', { label: 'Belege', ...INV, col2: 'ÉMETTEUR' }),
    nc: category('nc', { label: 'Notes de crédit', ...INV, col2: 'ÉMETTEUR' }),
    pay: category('pay', PAY),
    // "Nach Mitarbeiter": one tile per employee (employeeId stands in for the month key). The browser lists tiles
    // in reverse (newest month first), so the server's A–Z order is reversed here to show A–Z; the tile badge shows initials.
    payE: {
      ...PAY,
      months: live.payEmployees.map((e) => ({ id: e.employeeId, num: ini(e.name), title: e.name, year: '', count: e.count, sum: Number(toCents(e.sum)) / 100 })).reverse(),
      rows: Object.fromEntries(Object.entries(live.rows.payE || {}).map(([k, docs]) => [k, docs.map(toRow)])),
    },
    ext: category('ext', { label: 'Extraits', unit: 'Auszüge', long: 'Kontoauszüge', col1: 'AUSZUG', col2: 'KONTO' }),
    adm: category('adm', { label: 'Abgaben', unit: 'Dok.', long: 'Abgaben', col1: 'REFERENZ', col2: 'STELLE' }),
  };
  // Combinations („in+bel“, „out+bel“ …) are merged on first use.
  const combo = (ck: string): Cat => {
    const cs = ck.split('+') as DocumentCategory[];
    return merged(cs, { label: cs.map((c) => DIR_LABEL[c] ?? c).join(' + '), ...INV, col2: cs.includes('out') ? (cs.length > 1 ? 'ÉMETTEUR / KUNDE' : 'KUNDE') : 'ÉMETTEUR' });
  };
  const allCats = new Proxy(cats, { get: (t, k) => (typeof k === 'string' && k.includes('+') ? (t[k] ??= combo(k)) : t[k as string]) });
  const sept = live.workflow.map(toRow);
  const demo = [...live.queue.map(queueRow), ...live.today.map(paidRow)];
  return { src, cats: allCats, sept, demo };
}

/** Open credit notes as the design's `ncs` ({ id, no, sup, amt, dec }). */
export function creditNotes() {
  return live.creditNotes
    .filter((d) => d.status !== 'ARCHIVED')
    .map((d) => {
      // Imported credit notes carry a negative total: offer what is still open, as a positive amount.
      const t = toCents(d.totalAmount), c = d.openCredit != null ? toCents(d.openCredit) : t < 0n ? -t : t;
      return { id: d.id, no: d.documentNumber || '—', sup: d.issuerName || '—', amt: Number(c) / 100, dec: toDecimal(c) };
    })
    .filter((n) => n.amt > 0);
}

export { sumCents };
